import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CAROUSEL, CAROUSEL_CARD, CONTENT_CARD, FORM_STEP, GROUP, MENU, PAGE, SECTION_COLUMN } from 'constants/widgetTypes';

import { toggleEditMode, updateWidgetProperty, updateWidgetContent, updateBulkWidgetProperty } from 'actions/editor';
import { calculateBoundsForElement, getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType, isTopParentIsHotspot } from 'components/Editor/helper/widget';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';

import { Property } from '../../components/properties/';
import { LiveElementCreationProperty } from '../../components/liveElements';

import { Line } from '@paperflite-marketing/cleverstory-commons';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { useHotspotContainerSelector, useAllowCreateLiveElement } from 'components/Editor/hooks/useEditorSession';
import { HotspotActionButton, HotspotCreationProperty } from '../../components/hotspot';

import styles from './line.module.scss';

const LineWidget = React.memo(function LineWidget({ className, style, widgetProps, widgetRef }) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const widgetType = _.get(widgetProps, 'type');

  const editMode = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`) === id
  });

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, `properties.${breakpoint}`, {});
  const defaultProperties = _.get(widgetProps, `properties.desktop`, {});

  const type = _.get(widgetProps, `properties.desktop.type`);
  const stroke = _.get(defaultProperties, `stroke`);
  const fillColor = _.get(defaultProperties, `fill`);
  const legacyStyle = _.get(defaultProperties, `legacyStyle`);

  let Styles = {
    ...style,
    marginTop: legacyStyle ? '10px': 'unset'
  }

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.lineWidget)}
      style={Styles}
      data-widget-id={id}
      data-widget-type={widgetType}
      data-selectable={true}
    >
      <Animation widget={widgetProps}>
        <Line type={type} fill={fillColor} stroke={stroke} />
      </Animation>
    </div>
  )
});

const LineWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('style')

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const template = _.get(props, 'template.widget') != null;

  const handleEditMode = (view) => {
    setView(view);
    dispatch(toggleEditMode(editorSession, props.uid, true));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }
  const handleLiveElement = () => {
    setView('liveElements')
  }

  return (
    <>
      <Property.Action onClick={() => handleEditMode('style')} tooltip='Customize' className={styles.iconButton}>
        <i className={'cleverstory-customize'} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={'cleverstory-animations'} />
      </Property.Action>

      {!template && allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }

       {editMode && view === 'style' &&
        <LineStyleEditor
          widgets={[props]}
          onCancel={() => closePopup()}
        />
      }

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const LineWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('style')

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const template = _.get(props, 'template.widget') != null;

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} toltip='Animation' onClick={() => changeView('animation')}>
        <i className={'cleverstory-animations'} />
      </Property.Action>
      
      {!template && allowLiveElement && 
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }
    </>
  );
}

LineWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_CARD);
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ["e", "w"],
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('height', 'auto');
      return frame.get();
    },
    onReparent: (widget, parent) => {
      let hasTopParentIsHotspot = false;
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      if (parentType === CAROUSEL_CARD){
        hasTopParentIsHotspot = isTopParentIsHotspot(editorSession, parent, CAROUSEL);
      }

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CAROUSEL_CARD, MENU, FORM_STEP].includes(parentType);

      if (parentWidget || group || denyHostpotReparent || (hasHotspotChild && hasTopParentIsHotspot)) {
        return false;
      }

      return true;
    }
  }
}

LineWidget.actions = (editor, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return (
      <LineWidgetDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  if (breakpoint === 'mobile') {
    return (
      <LineWidgetMobileAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  return false;
}


export const LineStyleEditor = ({ widgets, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const [strokeState, setStrokeState] = useState({
      color: null,
      width: null
  });

  const widget = widgets[0];
  const id = _.get(widget, 'uid');

  const isBulk = widgets.length > 1;
  const widgetProps = isBulk ? strokeState : _.get(widget, `properties.${breakpoint}.stroke`);

  const stroke = widgetProps;
  const strokeColor = widgetProps.color ?? null;
  const strokeWidth = widgetProps.width ?? null;

  const handlePropertyChange = (value) => {
    const widgetIds = widgets.map(w => w.uid);
    if(isBulk) {
      setStrokeState(p => ({
        ...p,
        ...value?.stroke || {},
      }));
    }
    dispatch(updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, value));
  }

  const handleStrokeColorChange = (color) => {
    handlePropertyChange({
      stroke: {
        ...stroke,
        color: color
      }
    });
  }

  const handleStrokeWidthChange = (value) => {
    handlePropertyChange({
      stroke: {
        ...stroke,
        width: value
      }
    });
  }


  return (
    <Property.Dialog className={styles.styleDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Shape'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property.Color title={'Stroke Color'} value={strokeColor} onChange={handleStrokeColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={1}
            max={15}
            step={1}
            input
            value={strokeWidth}
            onChange={handleStrokeWidthChange}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

export { LineWidget };
